import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  makeStyles
} from '@material-ui/core';
import { RepliconDialog, User } from '~/modules/common/components';
import { AllocationDistributionMode } from '~/types';

const useStyles = makeStyles(theme => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    rowGap: theme.spacing(2),
    padding: theme.spacing(0, 3, 2)
  },
  dialogTitle: {
    padding: theme.spacing(2, 3)
  }
}));

const useDialogStyles = makeStyles(() => ({
  paper: {
    width: '520px'
  }
}));

const useUserStyles = makeStyles(theme => ({
  container: {
    overflow: 'hidden',
    padding: theme.spacing(0)
  },
  label: {
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.text.primary,
    paddingTop: theme.spacing(0.25)
  }
}));

const dialogTitleId = 'resource-over-allocation-confirmation-dialog-title';
const dialogContentId =
  'resource-over-allocation-confirmation-dialog-description';

const ResourceOverAllocationConfirmationDialog = ({
  confirmButtonLabel,
  onClose,
  onConfirm,
  user
}) => {
  const classes = useStyles();
  const dialogClasses = useDialogStyles();
  const userClasses = useUserStyles();

  const handleConfirm = useCallback(
    () => onConfirm(undefined, AllocationDistributionMode.Redistribute),
    [onConfirm]
  );

  return (
    <RepliconDialog
      aria-labelledby={dialogTitleId}
      aria-describedby={dialogContentId}
      classes={dialogClasses}
      open
      onClose={onClose}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography variant="h6" role="presentation" id={dialogTitleId}>
          <FormattedMessage id="resourceOverAllocationConfirmationDialog.title" />
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent} id={dialogContentId}>
        <User classes={userClasses} user={user} />
        <Typography variant="body1">
          <FormattedMessage id="resourceOverAllocationConfirmationDialog.warningMessage" />
        </Typography>
        <Typography variant="body1" color="textSecondary">
          <FormattedMessage id="resourceOverAllocationConfirmationDialog.confirmationMessage" />
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <FormattedMessage id="resourceOverAllocationConfirmationDialog.cancel" />
        </Button>
        <Button onClick={handleConfirm} color="primary" autoFocus>
          {confirmButtonLabel || (
            <FormattedMessage id="resourceAssignmentDialog.createAllocation" />
          )}
        </Button>
      </DialogActions>
    </RepliconDialog>
  );
};

ResourceOverAllocationConfirmationDialog.propTypes = {
  confirmButtonLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  user: PropTypes.object
};

export default ResourceOverAllocationConfirmationDialog;
